import { useFormik } from "formik"
import { useSettings } from "providers/SettingsProvider/SettingsProvider"
import { useUI } from "providers/UIProvider"
import { useEffect, useMemo } from "react"
import { useLocation, useParams } from "react-router-dom"
import { ILaundryCancelCommission } from "types/laundry"
import { BOOKING_FEE_TABS, CANCELLATION_FEE_TABS, TType } from "types/shared"
import { generateFeeHistoryFromCancellationFees } from "utils/feeHistoryUtils"
import * as Yup from "yup"
import useCompanyBranches from "./useCompanyBranches"

const CANCELLATION_FEE_INITIAL_VALUES = {
  time1: "",
  amount1: "",
  time2: "",
  amount2: "",
  time3: "",
  amount3: "",
  placeholderTime1: "",
  placeholderAmount1: "",
  placeholderTime2: "",
  placeholderAmount2: "",
  placeholderTime3: "",
  placeholderAmount3: ""
}

const CANCELLATION_FEE_SCHEMA = (T: TType) => Yup.object({
  time1: Yup
        .number().required(T("cancellation_fee.form.validations.time_required"))
        .max(60, T("cancellation_fee.form.validations.time_invalid")),
  amount1: Yup.number().required(T("cancellation_fee.form.validations.amount_required")),
  time2: Yup
        .number().required(T("cancellation_fee.form.validations.time_required"))
        .max(60, T("cancellation_fee.form.validations.time_invalid")),
  amount2: Yup.number().required(T("cancellation_fee.form.validations.amount_required")),
  time3: Yup
        .number().required(T("cancellation_fee.form.validations.time_required"))
        .max(60, T("cancellation_fee.form.validations.time_invalid")),
  amount3: Yup.number().required(T("cancellation_fee.form.validations.amount_required"))
})

const useCancellationFee = () => {
  const params = useParams()
  const { pathname }  = useLocation()

  const { T, toggleModal, modals } = useUI()
  const { selectedBranches, updateSelectedBranches } =  useCompanyBranches()
  const { cancelCommissions, cancelCommissionsTabType, handleChangeCancelCommission, handleChangeCancelCommissionsSubmit, cancelCommissionsHistory } = useSettings()

  const laundryId = Number(params?.laundryId) || Number(modals.cancelReservationCommissionModal.id) || null
  
  const onSubmit = (values: any) => {
    const cancelCommissionsArray = Array.from({ length: 3 }, (_v, i: number) => ({
      sortId: i + 1,
      commissionType: cancelCommissionsTabType === CANCELLATION_FEE_TABS.PERCENT ? 1 : 2,
      commissionTypeString: BOOKING_FEE_TABS.PERCENT,
      commission: values[`amount${i+1}`],
      cancelBeforeMinute: values[`time${i+1}`]
    }))
    if(pathname === "/settings/cancellation_fee") {
      toggleModal("feeHistoryModal", true, {
        data: generateFeeHistoryFromCancellationFees({
          T,
          commissionData: cancelCommissionsArray,
          inputData: cancelCommissionsHistory,
          selectedBranches
        }),
        handleSubmit: () => {
          handleChangeCancelCommissionsSubmit(selectedBranches, cancelCommissionsArray)
          resetForm()
          updateSelectedBranches([])
        }
      })
    } else if(laundryId !== null) {
      handleChangeCancelCommissionsSubmit([laundryId], cancelCommissionsArray)
    }
  }

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setValues, resetForm } = useFormik({
    initialValues: CANCELLATION_FEE_INITIAL_VALUES,
    validationSchema: () => CANCELLATION_FEE_SCHEMA(T),
    onSubmit
  })
  
  useEffect(()=>{
    if(cancelCommissions.length >= 3 && pathname !== "/settings/cancellation_fee") {
      setValues({
        time1: "",
        amount1: "",
        time2: "",
        amount2: "",
        time3: "",
        amount3: "",
        placeholderTime1: cancelCommissions[0].cancelBeforeMinute.toString(),
        placeholderAmount1:  `${cancelCommissions[0].commission.toString()} ${cancelCommissions[0].commissionType === 1 ? "%" : "ლ"}`,
        placeholderTime2: cancelCommissions[1].cancelBeforeMinute.toString(),
        placeholderAmount2: `${cancelCommissions[1].commission.toString()} ${cancelCommissions[1].commissionType === 1 ? "%" : "ლ"}`,
        placeholderTime3: cancelCommissions[2].cancelBeforeMinute.toString(),
        placeholderAmount3: `${cancelCommissions[2].commission.toString()} ${cancelCommissions[2].commissionType === 1 ? "%" : "ლ"}`
      })
    } else if(cancelCommissions.length >= 3 && pathname === "/settings/cancellation_fee" && selectedBranches.length === 1){
      setValues({
        time1: "",
        amount1: "",
        time2: "",
        amount2: "",
        time3: "",
        amount3: "",
        placeholderTime1: cancelCommissions[0].cancelBeforeMinute.toString(),
        placeholderAmount1:  `${cancelCommissions[0].commission.toString()} ${cancelCommissions[0].commissionType === 1 ? "%" : "ლ"}`,
        placeholderTime2: cancelCommissions[1].cancelBeforeMinute.toString(),
        placeholderAmount2: `${cancelCommissions[1].commission.toString()} ${cancelCommissions[1].commissionType === 1 ? "%" : "ლ"}`,
        placeholderTime3: cancelCommissions[2].cancelBeforeMinute.toString(),
        placeholderAmount3: `${cancelCommissions[2].commission.toString()} ${cancelCommissions[2].commissionType === 1 ? "%" : "ლ"}`
      })
    } else if(cancelCommissions.length >= 3 && pathname === "/settings/cancellation_fee" && selectedBranches.length === 0) {
      setValues({
        time1: "",
        amount1: "",
        time2: "",
        amount2: "",
        time3: "",
        amount3: "",
        placeholderTime1: "Choose Laundry",
        placeholderAmount1: "Choose Laundry",
        placeholderTime2: "Choose Laundry",
        placeholderAmount2: "Choose Laundry",
        placeholderTime3: "Choose Laundry",
        placeholderAmount3: "Choose Laundry"
      })
    } else if(cancelCommissions.length >= 3 && pathname === "/settings/cancellation_fee" && selectedBranches.length > 1)
    {
      setValues({
        time1: "",
        amount1: "",
        time2: "",
        amount2: "",
        time3: "",
        amount3: "",
        placeholderTime1: "Choose One Laundry",
        placeholderAmount1: "Choose One Laundry",
        placeholderTime2: "Choose One Laundry",
        placeholderAmount2: "Choose One Laundry",
        placeholderTime3: "Choose One Laundry",
        placeholderAmount3: "Choose One Laundry"
      })
    }
  },[cancelCommissions, selectedBranches])
  useEffect(()=>{
    if(pathname === "/settings/cancellation_fee" && selectedBranches.length === 0) {
      const initialCancelCommissions: ILaundryCancelCommission[] = Array.from({ length: 3 }, (_v, i: number) => ({
        sortId: i,
        commissionType: 1,
        commissionTypeString: BOOKING_FEE_TABS.PERCENT,
        commission: "",
        cancelBeforeMinute: ""
      }))
      handleChangeCancelCommission(initialCancelCommissions)
    }
  },[])
  return useMemo(
    () => ({
      form: {
        values, touched, errors, handleChange, handleBlur, handleSubmit, resetForm, setValues
      }
    }),
    [values, errors, touched],
  )
}

export default useCancellationFee